/**
 * @file UUID utility functions.
 * @author Knut Sveidqvist, Andreas Hellström
 * @desc  UUID created with this module complies with the RFC4122 standard.
 * Source: http://www.ietf.org/rfc/rfc4122.txt
 */

/**
 * Create a unique UUID according to RFC4122 standard.
 * Sources:
 *   http://caniuse.com/#search=crypto.getRandomValues
 *   https://www.ietf.org/rfc/rfc4122.txt
 *   https://www.npmjs.com/package/node-uuid
 *   http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript *
 * @returns {string} A unique UUID according to RFC4122 standard
 */
export const createUUID = () => {
  const crypt = window.crypto || window.msCrypto;
  if (crypt && crypt.getRandomValues) {
    return createUUIDWithBuiltinCryptoLibrary();
  }

  return createUUIDWithJSMathLibrary();
};

/**
 * Create a random UUID according to RFC4122 standard, using random numbers
 * from browser builtin Crypto or MS Crypto library.
 * @returns {string} A random UUID according to RFC4122 standard
 */
export function createUUIDWithBuiltinCryptoLibrary() {
  const crypt = window.crypto || window.msCrypto;
  const rnd = new Uint16Array(8);
  crypt.getRandomValues(rnd);
  rnd[3] = (rnd[3] & 0x0fff) | 0x4000; // eslint-disable-line
  rnd[4] = (rnd[4] & 0x3f00) | 0x8000; // eslint-disable-line
  const uuid = new Array(8);
  for (let i = 0; i < 8; i++) {
    uuid[i] = `0000${rnd[i].toString(16)}`.slice(-4);
  }
  return [uuid[0] + uuid[1], uuid[2], uuid[3], uuid[4], uuid[5] + uuid[6] + uuid[7]].join('-');
}

/**
 * Create a random UUID according to RFC4122 standard, using random numbers
 * from JavaScript Math library.
 * @returns {string} A random UUID according to RFC4122 standard
 */
function createUUIDWithJSMathLibrary() {
  const hexDigits = '0123456789abcdef';
  const s = [];
  let i;

  for (i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010

  // eslint-disable-next-line no-bitwise
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the

  // clock_seq_hi_and_reserved to 01
  s[8] = '-';
  s[13] = '-';
  s[18] = '-';
  s[23] = '-';

  return s.join('');
}
